import { useState, useRef, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Globe, ChevronDown, Search, Check } from 'lucide-react'
import { useNetwork } from '../contexts/NetworkContext'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group'

type SearchableNetwork = { id: string; name: string; tagId: string }

/** Lower-cased with all whitespace removed, so "ad nuntius" finds "Adnuntius" and vice versa. */
function normalize(text: string) {
  return text.replace(/\s+/g, '').toLowerCase()
}

/** Whether a network's name, tag id or id contains the (normalized) query. */
function matchesNetwork(network: SearchableNetwork, query: string) {
  return (
    normalize(network.name).includes(query) ||
    network.tagId.toLowerCase().includes(query) ||
    network.id.toLowerCase().includes(query)
  )
}

/** The tag id or id a search matched, when it did not match the network's name. */
function matchedIdentifier(network: SearchableNetwork, query: string) {
  if (!query || normalize(network.name).includes(query)) return null
  if (network.tagId.toLowerCase().includes(query)) return network.tagId
  if (network.id.toLowerCase().includes(query)) return network.id
  return null
}

export function NetworkSwitcher() {
  const { t } = useTranslation()
  const {
    networks,
    currentNetwork,
    loading,
    switchNetwork,
    extraNetworkTagIds,
    toggleExtraNetwork,
    clearExtraNetworks,
    extraNetworksRemaining,
  } = useNetwork()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [highlightIdx, setHighlightIdx] = useState(-1)
  const ref = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  useEffect(() => {
    if (open) {
      setSearch('')
      setHighlightIdx(-1)
      setTimeout(() => searchRef.current?.focus(), 0)
    }
  }, [open])

  // Networks are just as often known by their tag id or id as by their name, so
  // the search covers all three.
  const query = normalize(search)
  const filtered = useMemo(() => {
    if (!query) return networks
    return networks.filter((n) => matchesNetwork(n, query))
  }, [networks, query])

  // Reset highlight when filtered list changes
  useEffect(() => {
    setHighlightIdx(-1)
  }, [filtered])

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIdx < 0 || !listRef.current) return
    const items = listRef.current.querySelectorAll('.ad-netsw__item')
    items[highlightIdx]?.scrollIntoView({ block: 'nearest' })
  }, [highlightIdx])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIdx((i) => (i < filtered.length - 1 ? i + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIdx((i) => (i > 0 ? i - 1 : filtered.length - 1))
    } else if (e.key === 'Enter' && highlightIdx >= 0 && filtered[highlightIdx]) {
      e.preventDefault()
      const n = filtered[highlightIdx]
      if (n.tagId !== currentNetwork?.tagId) switchNetwork(n.tagId)
      setOpen(false)
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  if (!loading && networks.length <= 1) return null

  if (loading) {
    return (
      <div className="ad-netsw">
        <button className="ad-netsw__trigger" disabled title={t('network.loadingNetworks')}>
          <Loader2 className="size-4 animate-spin" />
          <span className="ad-netsw__name">{t('network.loading')}</span>
        </button>
      </div>
    )
  }

  const extraCount = extraNetworkTagIds.length

  return (
    <div className="ad-netsw" ref={ref}>
      <button
        className="ad-netsw__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={t('network.switchNetwork')}
      >
        <Globe className="size-4" />
        <span className="ad-netsw__name">{currentNetwork?.name ?? '—'}</span>
        {extraCount > 0 && (
          <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-primary">
            +{extraCount}
          </span>
        )}
        <ChevronDown className={`size-3 ad-netsw__chev${open ? ' ad-netsw__chev--open' : ''}`} />
      </button>

      {open && (
        <div className="ad-netsw__menu w-[300px]">
          <div className="ad-netsw__head">{t('network.networksHeading')}</div>
          <div className="ad-netsw__search">
            <InputGroup className="h-8 text-xs">
              <InputGroupAddon>
                <Search className="size-3.5" />
              </InputGroupAddon>
              <InputGroupInput
                ref={searchRef}
                placeholder={t('network.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                className="text-xs"
              />
            </InputGroup>
          </div>
          <div className="ad-netsw__list" ref={listRef}>
            {filtered.map((n, i) => {
              const isPrimary = n.tagId === currentNetwork?.tagId
              const isExtra = extraNetworkTagIds.includes(n.tagId)
              const atLimit = !isExtra && extraNetworksRemaining <= 0
              const matchedId = matchedIdentifier(n, query)
              return (
                <div
                  key={n.id}
                  className={`ad-netsw__item${isPrimary ? ' is-active' : ''}${i === highlightIdx ? ' is-highlighted' : ''}`}
                  onMouseEnter={() => setHighlightIdx(i)}
                >
                  {/* Row body switches the primary network; the checkbox only adds the
                      network to the advertising lists, so the two must not overlap. */}
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-2 bg-transparent p-0 text-left font-[inherit] text-[inherit] text-inherit"
                    onClick={() => {
                      if (!isPrimary) switchNetwork(n.tagId)
                      setOpen(false)
                    }}
                  >
                    <span className="ad-netsw__item-name">{n.name}</span>
                    {/* Shown when the row is only here because of its tag id or id,
                        so it is clear why the search matched it. */}
                    {matchedId && (
                      <span className="truncate font-mono text-[10.5px] text-muted-foreground">{matchedId}</span>
                    )}
                    {isPrimary && <Check className="size-4 ad-netsw__check" />}
                  </button>
                  <Checkbox
                    checked={isPrimary || isExtra}
                    disabled={isPrimary || atLimit}
                    onCheckedChange={() => toggleExtraNetwork(n.tagId)}
                    aria-label={
                      isPrimary
                        ? t('network.primaryNetwork')
                        : t('network.includeNetwork', { name: n.name })
                    }
                    title={
                      isPrimary
                        ? t('network.primaryNetwork')
                        : atLimit
                          ? t('network.extraLimitReached', { max: extraNetworksRemaining + extraCount })
                          : t('network.includeNetwork', { name: n.name })
                    }
                  />
                </div>
              )
            })}
            {filtered.length === 0 && (
              <div className="ad-netsw__empty">{t('network.noNetworks')}</div>
            )}
          </div>
          <div className="mt-1 border-t px-2.5 pb-1 pt-2">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">
                {t('network.includeLabel')}
              </span>
              {extraCount > 0 && (
                <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={clearExtraNetworks}>
                  {t('network.clearExtras')}
                </Button>
              )}
            </div>
            <p className="mt-1 text-xs leading-snug text-muted-foreground">{t('network.includeHint')}</p>
          </div>
        </div>
      )}
    </div>
  )
}
